import React, { useState } from "react";

import { OutlinedTextFieldProps } from "@mui/material";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTime } from "luxon";

interface DatePickerProps {
  defaultDateSelection?: Date;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  // Callers currently assume a non-null Date and don't handle a
  // cleared/invalid input -- widening this to `Date | null` is the
  // technically correct type (the underlying picker can call onChange(null)
  // on keyboard-clear) but surfaces null-safety gaps in ~9 unrelated pages,
  // so left loose here until each caller is audited/fixed individually.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (date: any) => void;
  label?: string;
  size?: "small" | "medium" | undefined;
  fullWidth?: boolean;
}

interface DatePickerTextFieldProps extends OutlinedTextFieldProps {
  "data-testid"?: string;
}

const DatePicker = ({
  defaultDateSelection,
  disabled,
  minDate,
  maxDate,
  onChange,
  label,
  size,
  fullWidth,
}: DatePickerProps) => {
  const [selectedPickerDate, setSelectedPickerDate] = useState<Date | null>(
    defaultDateSelection ? new Date(defaultDateSelection) : new Date(),
  );

  const onDateChange = (date: DateTime | null) => {
    const newDateTime = date ? date.toJSDate() : null;
    const currentTime = DateTime.now().toJSDate();

    if (newDateTime) {
      newDateTime.setHours(
        defaultDateSelection
          ? defaultDateSelection.getHours()
          : currentTime.getHours(),
      );
      newDateTime.setMinutes(
        defaultDateSelection
          ? defaultDateSelection.getMinutes()
          : currentTime.getMinutes(),
      );
      newDateTime.setSeconds(
        defaultDateSelection
          ? defaultDateSelection.getSeconds()
          : currentTime.getSeconds(),
      );
      newDateTime.setMilliseconds(
        defaultDateSelection
          ? defaultDateSelection.getMilliseconds()
          : currentTime.getMilliseconds(),
      );
    }

    setSelectedPickerDate(newDateTime);
    onChange(newDateTime);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <DesktopDatePicker
        label={label || ""}
        minDate={minDate ? DateTime.fromJSDate(minDate) : undefined}
        maxDate={maxDate ? DateTime.fromJSDate(maxDate) : undefined}
        value={
          selectedPickerDate ? DateTime.fromJSDate(selectedPickerDate) : null
        }
        onChange={onDateChange}
        slotProps={{
          textField: {
            size: size,
            fullWidth: fullWidth,
            "data-testid": "date-picker-input-field",
          } as DatePickerTextFieldProps,
        }}
        disabled={disabled}
      />
    </LocalizationProvider>
  );
};

export default DatePicker;
